/* eslint-disable max-len */
import React, { useEffect, createRef, Suspense } from 'react';
import { observer } from 'mobx-react-lite';
import { BypassBlock, classNames, ToastProvider } from '@groma-dev/blueprint';
import { initializeAuthInterceptor, parseLocationSearch, SiteExp } from '@groma-dev/scaffold';
import { useLocation, Routes, Route } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import { AppConfig } from '../../../core/AppConfig';
import { jumpToAnchorRef, scrollToTop } from '../../utils/WebHelper';
import { useDataStore } from '../../providers/StoresProvider';
import { ComponentFactory } from '../../ComponentFactory';
import { AppRoutes } from '../../../core/routes';
import { APIClient } from '../../../core/apiclient/APIClient';
import AuthRouter from '../authrouter/AuthRouter';

import { getAppStyles } from './styles/App.styles';

const Auth = React.lazy(() => ComponentFactory.AuthAsync);
const Overview = React.lazy(() => ComponentFactory.OverviewAsync);
const ErrorPage = React.lazy(() => ComponentFactory.ErrorPageAsync);
const Properties = React.lazy(() => ComponentFactory.PropertiesAsync);
const PropertyNft = React.lazy(() => ComponentFactory.PropertyNftAsync);

let authRedirectInitialized = false;

export interface AppProps {}

const AppContent = observer(() => {
  const styles = getAppStyles();
  const dataStore = useDataStore();
  const { userState } = dataStore;
  const { search } = useLocation();
  const { kiosk: isKioskDisplay } = parseLocationSearch(search);

  return userState.fetchingUser ? (
    <ComponentFactory.Loading isPage />
  ) : (
    <>
      {!isKioskDisplay && <ComponentFactory.Header />}
      <div id="mainContent" className={styles.mainContent}>
        <ComponentFactory.ErrorBoundary>
          <Routes>
            <Route path={`${AppRoutes.root}`} element={<Overview />} />
            <Route path={AppRoutes.Properties}>
              <Route index element={<Properties />} />
              <Route path={AppRoutes.PropertyDetails} element={<PropertyNft />} />
            </Route>
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </ComponentFactory.ErrorBoundary>
      </div>
      {!isKioskDisplay && <ComponentFactory.Footer />}
    </>
  );
});

const App = observer(() => {
  const styles = getAppStyles();
  const { pathname, search, hash } = useLocation();
  const { Authentication, Domains } = AppConfig.Settings;
  const { name: authCookieName, domain } = Authentication.cookie;
  const resetRef = createRef<HTMLSpanElement>();

  if (Cookies.get(authCookieName) && !authRedirectInitialized) {
    const returnUrl = encodeURIComponent(`/${search}`);
    initializeAuthInterceptor(
      APIClient,
      `${Domains.id.baseUrl}/signin?return_url=${returnUrl}&site_exp=${SiteExp.HOLDINGS}`,
      {
        name: authCookieName,
        domain,
      }
    );
    authRedirectInitialized = true;
  }

  useEffect(() => {
    if (hash) jumpToAnchorRef();
    else {
      scrollToTop();
      // Move focus to top of the page if no hash in url
      if (!hash) resetRef.current?.focus();
    }
  }, [pathname]);

  return (
    <>
      <span ref={resetRef} tabIndex={-1} />
      <BypassBlock id="mainContent">
        <FormattedMessage
          id="common.bypassBlocks.mainContent"
          defaultMessage="Skip to Main Content"
          description="Main content bypass block"
        />
      </BypassBlock>
      <ToastProvider>
        <div className={classNames(styles.mainContainer)}>
          <AuthRouter>
            <ComponentFactory.ErrorBoundary>
              <Suspense fallback={<ComponentFactory.Loading isPage />}>
                <Routes>
                  <Route path={AppRoutes.Auth} element={<Auth />} />
                  <Route path="*" element={<AppContent />} />
                </Routes>
              </Suspense>
            </ComponentFactory.ErrorBoundary>
          </AuthRouter>
        </div>
      </ToastProvider>
    </>
  );
});

export default App;
